import React from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

type MahngebuehrenMap = Record<string, number>; // "mm.yyyy": number

const parseMmYyyy = (s: string) => {
  const [mm, yyyy] = s.split(".");
  const m = Number(mm);
  const y = Number(yyyy);
  return new Date(y, m - 1, 1).getTime();
};

const formatNumber = (n: number) => new Intl.NumberFormat("de-DE").format(n);

const getSuggestedMax = (maxVal: number) => {
  if (maxVal <= 0) return 10;
  const padded = maxVal * 1.15;
  const pow = Math.pow(10, Math.floor(Math.log10(padded)));
  return Math.ceil(padded / pow) * pow;
};

const MahnwesenBarChart: React.FC<{
  data: MahngebuehrenMap;
  height?: number;
  label?: string;
}> = ({ data, height = 340, label = "Mahngebühren" }) => {
  const labels = Object.keys(data).sort(
    (a, b) => parseMmYyyy(a) - parseMmYyyy(b)
  );
  const values = labels.map((k) => Number(data[k] ?? 0));
  const maxVal = values.length ? Math.max(...values) : 0;

  const chartData = {
    labels,
    datasets: [
      {
        label,
        data: values,
        backgroundColor: "#44AEB0",
        borderColor: "#44AEB0",
        borderWidth: 2,
        barThickness: 24,
        maxBarThickness: 28,
      },
    ],
  };

  return (
    <div style={{ height, width: "100%", overflowX: "auto" }}>
      <div
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "x",
            interaction: { mode: "index", intersect: false },
            scales: {
              x: {
                grid: { display: false },
                ticks: { maxRotation: 0, autoSkip: false }, // show all month labels
              },
              y: {
                beginAtZero: true,
                suggestedMax: getSuggestedMax(maxVal),
                ticks: {
                  callback: (v) => formatNumber(Number(v as number)),
                },
              },
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    `${label}: ${formatNumber(Number(ctx.parsed.y))} €`,
                },
              },
              datalabels: {
                anchor: "end",
                align: "end",
                clamp: true,
                clip: false,
                offset: 4,
                color: "#444",
                formatter: (value: number) => `${formatNumber(value)} €`,
              },
            },
          }}
          plugins={[ChartDataLabels]}
        />
      </div>
    </div>
  );
};

export default MahnwesenBarChart;
